/**
 * Interactive Map Module
 * Creates a Leaflet map showing IP geolocation data
 * Includes GDPR consent management for OpenStreetMap
 */

import L from 'leaflet';
import Papa from 'papaparse';
import type { Location, CityCoordinates } from '../types/ipgeo';
import { setConsent, getConsentStatus, resetConsent, ConsentStatus } from './consent';

// City coordinates lookup table
const CITY_COORDINATES: CityCoordinates = {
  'Frankfurt': [50.1109, 8.6821],
  'Dortmund': [51.5136, 7.4653],
  'Iserlohn': [51.3761, 7.7003],
  'Nuremberg': [49.4521, 11.0767],
  'Las Vegas': [36.1699, -115.1398],
  'Newark': [40.7357, -74.1724]
};

// Country flag emojis
const COUNTRY_FLAGS: Record<string, string> = {
  'DE': '🇩🇪',
  'US': '🇺🇸'
};

// Marker colors by country
const MARKER_COLORS: Record<string, string> = {
  'DE': '#3b82f6', // Blue for Germany
  'US': '#f59e0b'  // Orange for USA
};

let map: L.Map | null = null;
let markers: L.Marker[] = [];

/**
 * Initialize the map with GDPR consent check
 */
export async function initializeMap(): Promise<void> {
  const consentStatus = getConsentStatus();

  if (consentStatus === ConsentStatus.GRANTED) {
    // User has already given consent, load map
    await loadMap();
  } else if (consentStatus === ConsentStatus.DENIED) {
    // User declined consent, show message
    showConsentDeclinedMessage();
  } else {
    // No decision yet, show consent banner
    showConsentBanner();
  }
}

/**
 * Show the consent banner
 */
function showConsentBanner(): void {
  const consentOverlay = document.getElementById('mapConsent');
  const acceptButton = document.getElementById('consentAccept');
  const declineButton = document.getElementById('consentDecline');

  if (consentOverlay) {
    consentOverlay.style.display = 'flex';
  }

  if (acceptButton) {
    acceptButton.addEventListener('click', handleConsentAccept);
  }

  if (declineButton) {
    declineButton.addEventListener('click', handleConsentDecline);
  }
}

/**
 * Handle user accepting consent
 */
async function handleConsentAccept(): Promise<void> {
  setConsent(true);
  hideConsentBanner();
  await loadMap();
}

/**
 * Handle user declining consent
 */
function handleConsentDecline(): void {
  setConsent(false);
  hideConsentBanner();
  showConsentDeclinedMessage();
}

/**
 * Hide the consent banner
 */
function hideConsentBanner(): void {
  const consentOverlay = document.getElementById('mapConsent');
  if (consentOverlay) {
    consentOverlay.style.display = 'none';
  }
}

/**
 * Show message when consent is declined
 */
function showConsentDeclinedMessage(): void {
  const mapContainer = document.getElementById('map');
  if (mapContainer) {
    mapContainer.innerHTML = `
      <div class="map-declined-message">
        <div>
          <p style="font-size: 1.25rem; margin-bottom: 0.5rem;">Map not loaded</p>
          <p style="font-size: 0.875rem; margin-bottom: 1rem;">You declined to load content from OpenStreetMap.</p>
          <button id="changeConsent" class="btn btn-primary" style="margin-top: 1rem;">Change Privacy Settings</button>
        </div>
      </div>
    `;

    // Add event listener to reset consent
    const changeButton = document.getElementById('changeConsent');
    if (changeButton) {
      changeButton.addEventListener('click', handleChangeConsent);
    }
  }
}

/**
 * Handle user wanting to change consent preference
 */
function handleChangeConsent(): void {
  // Reset the stored consent preference
  resetConsent();

  // Clear the map container first
  const mapContainer = document.getElementById('map');
  if (mapContainer) {
    mapContainer.innerHTML = '<div id="mapConsent" class="map-consent-overlay" style="display: none;"><div class="consent-card"><h3>Privacy Notice</h3><p>This interactive map loads content from OpenStreetMap, a third-party service. By enabling the map, you consent to loading data from external servers, which may process your IP address according to their <a href="https://wiki.osmfoundation.org/wiki/Privacy_Policy" target="_blank" rel="noopener">privacy policy</a>.</p><div class="consent-actions"><button id="consentAccept" class="btn btn-primary">Accept & Load Map</button><button id="consentDecline" class="btn btn-secondary">Decline</button></div></div></div>';
  }

  // Re-initialize the map (which will show the consent banner again)
  initializeMap();
}

/**
 * Load the actual map (called after consent is granted)
 */
async function loadMap(): Promise<void> {
  try {
    // Create the map centered on Europe initially
    map = L.map('map').setView([50.0, 10.0], 4);

    // Add OpenStreetMap tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(map);

    // Load and display location data
    await loadLocationData();

    console.log('Map initialized successfully');
  } catch (error) {
    console.error('Error initializing map:', error);
    showMapError();
  }
}

/**
 * Fetch and parse ipgeo.csv data
 */
async function loadLocationData(): Promise<void> {
  try {
    // Fetch the CSV file
    const response = await fetch('/ipgeo.csv');
    if (!response.ok) {
      throw new Error(`Failed to fetch ipgeo.csv: ${response.statusText}`);
    }

    const csvText = await response.text();

    // Parse CSV using PapaParse
    const parsed = Papa.parse(csvText, {
      skipEmptyLines: true,
      comments: '#'
    }) as Papa.ParseResult<string[]>;

    if (!parsed.data || parsed.data.length === 0) {
      throw new Error('No data found in ipgeo.csv');
    }

    // Process locations
    const locations = processLocations(parsed.data);

    // Add markers to map
    addMarkersToMap(locations);

    // Fit map bounds to show all markers
    if (markers.length > 0 && map) {
      const group = L.featureGroup(markers);
      map.fitBounds(group.getBounds().pad(0.1));
    }
  } catch (error) {
    console.error('Error loading location data:', error);
    throw error;
  }
}

/**
 * Process CSV data into location objects
 */
function processLocations(data: string[][]): Location[] {
  const locations: Location[] = [];

  data.forEach(row => {
    if (row.length >= 4 && row[0]) {
      const subnet = row[0];
      const country = row[1];
      const region = row[2];
      const city = row[3];

      // Get coordinates for this city
      const coordinates = CITY_COORDINATES[city];

      if (coordinates) {
        locations.push({
          subnet,
          country,
          region,
          city,
          coordinates
        });
      } else {
        console.warn(`No coordinates found for city: ${city}`);
      }
    }
  });

  return locations;
}

/**
 * Add markers to the map for each location
 */
function addMarkersToMap(locations: Location[]): void {
  if (!map) return;

  // Group locations by city to handle multiple subnets in same location
  const cityGroups: Record<string, Location[]> = {};

  locations.forEach(location => {
    if (!cityGroups[location.city]) {
      cityGroups[location.city] = [];
    }
    cityGroups[location.city].push(location);
  });

  // Create markers for each city
  Object.keys(cityGroups).forEach(city => {
    const locationGroup = cityGroups[city];
    const firstLocation = locationGroup[0];
    const [lat, lng] = firstLocation.coordinates;

    // Create custom icon with country-specific color
    const markerColor = MARKER_COLORS[firstLocation.country] || '#3b82f6';
    const icon = createCustomIcon(markerColor);

    // Create marker
    const marker = L.marker([lat, lng], { icon })
      .addTo(map!);

    // Create popup content
    const popupContent = createPopupContent(locationGroup);
    marker.bindPopup(popupContent);

    // Create tooltip (shows on hover)
    const flag = COUNTRY_FLAGS[firstLocation.country] || '';
    marker.bindTooltip(`${flag} ${city}`, {
      permanent: false,
      direction: 'top'
    });

    markers.push(marker);
  });
}

/**
 * Create custom marker icon
 */
function createCustomIcon(color: string): L.DivIcon {
  const svgIcon = `
    <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 8.75 12.5 28.5 12.5 28.5S25 21.25 25 12.5C25 5.596 19.404 0 12.5 0z"
            fill="${color}"
            stroke="#ffffff"
            stroke-width="2"/>
      <circle cx="12.5" cy="12.5" r="5" fill="#ffffff"/>
    </svg>
  `;

  return L.divIcon({
    html: svgIcon,
    className: 'custom-marker',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34]
  });
}

/**
 * Create popup content HTML for location group
 */
function createPopupContent(locationGroup: Location[]): string {
  const firstLocation = locationGroup[0];
  const flag = COUNTRY_FLAGS[firstLocation.country] || '';

  let html = `
    <div class="popup-title">${flag} ${firstLocation.city}</div>
    <div class="popup-detail"><strong>Region:</strong> ${firstLocation.region}</div>
    <div class="popup-detail"><strong>Country:</strong> ${firstLocation.country}</div>
    <div class="popup-detail"><strong>Subnet${locationGroup.length > 1 ? 's' : ''}:</strong></div>
    <ul style="margin: 0.5rem 0 0 1.25rem; padding: 0;">
  `;

  locationGroup.forEach(location => {
    html += `<li style="margin-bottom: 0.25rem;"><code>${location.subnet}</code></li>`;
  });

  html += `</ul>`;

  return html;
}

/**
 * Show error message in map container
 */
function showMapError(): void {
  const mapContainer = document.getElementById('map');
  if (mapContainer) {
    mapContainer.innerHTML = `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        background: #f8fafc;
        color: #64748b;
        text-align: center;
        padding: 2rem;
      ">
        <div>
          <p style="font-size: 1.25rem; margin-bottom: 0.5rem;">Unable to load map</p>
          <p style="font-size: 0.875rem;">Please check your internet connection and refresh the page.</p>
        </div>
      </div>
    `;
  }
}
